// ─────────────────────────────────────────────────────────────────────────────
// The gateway return leg.
//
// A top-up leaves the app: `window.location.href = gatewayUrl`. The payment BFF
// verifies and settles with the bank server-to-server — the browser never sees
// the receipt and could not settle one if it did — and then sends the doctor
// back to an in-app path carrying `?payment=success|failed`.
//
// Two things follow, and they are the whole reason this is a module rather than
// an effect inside `Credits`:
//
//   THE OUTCOME IS A MESSAGE, NOT A FACT. `?payment=success` is a hint from a
//   redirect anyone can type. The BALANCE is the truth, and it comes from a
//   refetch. So this decides what to SAY and never what to believe.
//
//   IT CAN LAND ANYWHERE. From an installed iOS PWA the gateway may open in a
//   different browsing context, and its redirect can arrive in one with no
//   `kc_token` — which sends the tab through Keycloak first. With the return
//   path remembered at boot (session.js), the doctor comes back to
//   `/credits?payment=…` and `Credits` reports it. But a return that lands on
//   any OTHER in-app route used to say nothing at all, so the shell handles the
//   same parameter wherever it appears.
// ─────────────────────────────────────────────────────────────────────────────

export const PAYMENT_PARAM = 'payment';

/**
 * @returns {'success'|'failed'|null}
 *
 * Anything that is not literally `success` is a failure: a gateway that invents
 * a new value must not be read as a payment that worked.
 */
export function readPaymentOutcome(search) {
  const raw = typeof search === 'string' ? search : window.location.search;
  const value = new URLSearchParams(raw).get(PAYMENT_PARAM);
  if (!value) return null;
  return value === 'success' ? 'success' : 'failed';
}

/**
 * Remove `?payment=` so a refresh cannot replay the message, and nothing else.
 *
 * Two details that used to be wrong here:
 *   • it replaced the whole search string with the bare pathname, which would
 *     drop any filter state the screen keeps in the URL;
 *   • it passed `{}` as the history state, wiping React Router's `idx`/`key`.
 *     The router computes its `popstate` delta from that index, and the overlay
 *     history integration depends on the same field — so clearing it made the
 *     next back press ambiguous.
 */
export function stripPaymentParam() {
  try {
    const url = new URL(window.location.href);
    if (!url.searchParams.has(PAYMENT_PARAM)) return;
    url.searchParams.delete(PAYMENT_PARAM);
    const next = `${url.pathname}${url.search}${url.hash}`;
    window.history.replaceState(window.history.state, '', next);
  } catch { /* nothing to strip */ }
}
